import { useMemo, useState } from "react";
import { MultiSelect } from "react-multi-select-component";

export interface Option {
    value: any;
    label: string;
    key?: string;
    disabled?: boolean;
}

function convertToInternalOption(option: any, optionlabel: any): Option {
    let label = '';
    if (optionlabel instanceof Function) {
        label = optionlabel(option);
    } else {
        label = option[optionlabel].toString();
    }

    return { label: label, value: option };
}

export interface SearchableSetSingleSelected<T> { (value?: T, id?: string): void }
export interface SearchableSetMultiSelected<T> { (value?: T[], id?: string): void }

export interface SearchableSingleSelectProps<T> {
    options: T[],
    selected: T | T[] | undefined,
    optionlabel: string | { (option: T): string },
    setSelected: SearchableSetSingleSelected<T>,
    type: 'single',
    defaultIsOpen?: boolean,
    id?: string
}

export interface SearchableMultiSelectProps<T> {
    options?: T[],
    selected: T | T[] | undefined,
    optionlabel: string | { (option: T): string },
    setSelected: SearchableSetMultiSelected<T>,
    type: 'multi',
    defaultIsOpen?: boolean,
    id?: string
}

const SearchableSelect = <T extends unknown>(
    { options, setSelected, selected, optionlabel, type, defaultIsOpen = false, id
    }:
        SearchableSingleSelectProps<T> | SearchableMultiSelectProps<T>) => {

    const [overrideStrings, setOverrideStrings] = useState({
        "allItemsAreSelected": "All items are selected.",
        "clearSearch": "Clear Search",
        "clearSelected": "Clear Selected",
        "noOptions": "No options",
        "search": "Search",
        "selectAll": "Select All",
        "selectAllFiltered": "Select All (Filtered)",
        "selectSomeItems": "Select...",
        "create": "Create",
    })

    const internalOptions = useMemo(() => {

        if (options?.length === 1) {
            setOverrideStrings((o) => {
                return {
                    ...o,
                    "allItemsAreSelected": convertToInternalOption(options[0], optionlabel).label
                }
            })
            // setSelected(options[0])
        } else {
            setOverrideStrings((o) => {
                return {
                    ...o,
                    "allItemsAreSelected": "All items are selected."
                }
            })
        }

        if (options) {
            return options.map(option => {
                return convertToInternalOption(option, optionlabel);
            })
        } else {
            return [];
        }


    }, [options, optionlabel]);

    const selectedOptions = useMemo(() => {
        if (selected) {
            if (Array.isArray(selected)) {
                return selected.map(option => {
                    return internalOptions.filter((io) => io.label === convertToInternalOption(selected, optionlabel).label)[0]
                })
            } else {
                return internalOptions.filter((io) => io.label === convertToInternalOption(selected, optionlabel).label)
            }
        } else {
            return [];
        }
    }, [selected, internalOptions, optionlabel]);


    function handleChange(newSelected: Option[]) {
        if (type === 'multi') {
            setSelected(newSelected.map(ns => ns.value), id);
        } else {
            if (newSelected.length > 1) {
                newSelected = newSelected.filter(o => o !== selectedOptions[0])
            }
            if (newSelected.length > 1) {
                newSelected = [newSelected[0]]
            }
            if (newSelected.length === 1) {
                setSelected(newSelected[0].value, id)
            } else {
                setSelected(undefined, id)
            }
        }
    }
    return <MultiSelect
        options={internalOptions}
        value={selectedOptions}
        onChange={handleChange}
        labelledBy="Select"
        hasSelectAll={false}
        closeOnChangedValue={type !== 'multi'}
        defaultIsOpen={defaultIsOpen}
        overrideStrings={overrideStrings}
    />
};

export default SearchableSelect;
